import { create } from 'zustand'

const initialQuizData = {
  question: '',
  questionImageFile: null,
  correctAnswer: '',
  correctAnswers: ['', '', '', '', ''],
  explanation: '',
  explanationImageFile: null,
  questionImageFile1: null,
  questionImageFile2: null,
  existingQuestionImageUrl: undefined,
  existingSubQuestionImageUrl: undefined,
  existingExplanationImageUrl: undefined,
  answerImageFile: null,
  answerImageFiles: [null, null, null, null, null],
  checkedStates: [false, false, false, false, false],
  isCorrect1: false,
  isCorrect2: false,
  isCorrect3: false,
  isCorrect4: false,
  isCorrect5: false,
}

const initialCurriculumData = {
  level: '',
  mainChapter: '',
  subChapter: '',
  unit: '',
  no: 1,
  questionLabel: '',
  selectedType: 'A',
}

export const useQuizStore = create((set, get) => ({
  originalData: null,
  curriculum: initialCurriculumData,
  quizData: initialQuizData,
  isModified: false,
  changedFields: new Set(),

  initializeFromData: (data) => {
    const curriculum = {
      level: data.LEVEL || '',
      mainChapter: data.MAIN || '',
      subChapter: data.SUB || '',
      unit: data.UNIT || '',
      detailUnit: data.DETAIL_UNIT || '',
      no: data.NO || 0,
      questionLabel: data.DEPTH || '',
      selectedType: data.TYPE || 'A',
    }

    const checkedStates = [
      data.IS_CORRECT_1 ?? false,
      data.IS_CORRECT_2 ?? false,
      data.IS_CORRECT_3 ?? false,
      data.IS_CORRECT_4 ?? false,
      data.IS_CORRECT_5 ?? false,
    ]

    const quizData = {
      question: data.TITLE || '',
      questionImageFile: null,
      correctAnswer: data.ANSWER1 || '',
      correctAnswers: [
        data.ANSWER1 || '',
        data.ANSWER2 || '',
        data.ANSWER3 || '',
        data.ANSWER4 || '',
        data.ANSWER5 || '',
      ],
      explanation: data.DESC || '',
      explanationImageFile: null,
      questionImageFile1: null,
      questionImageFile2: null,
      existingQuestionImageUrl: data.IMG_URL || '',
      existingSubQuestionImageUrl: data.IMG_URL2 || '',
      existingExplanationImageUrl: data.DESC_URL || '',
      answerImageFile: null,
      answerImageFiles: [null, null, null, null, null],
      checkedStates,
      isCorrect1: data.IS_CORRECT_1 ?? false,
      isCorrect2: data.IS_CORRECT_2 ?? false,
      isCorrect3: data.IS_CORRECT_3 ?? false,
      isCorrect4: data.IS_CORRECT_4 ?? false,
      isCorrect5: data.IS_CORRECT_5 ?? false,
    }

    set({
      originalData: { ...data },
      curriculum,
      quizData,
      isModified: false,
      changedFields: new Set(),
    })
  },

  updateCurriculum: (field, value) => {
    set((state) => {
      const newCurriculum = { ...state.curriculum, [field]: value }

      if (['level', 'mainChapter', 'subChapter', 'unit'].includes(field)) {
        newCurriculum.questionLabel = `${newCurriculum.level}-${newCurriculum.mainChapter}-${newCurriculum.subChapter}-${newCurriculum.unit}`
      }

      const newChangedFields = new Set(state.changedFields)
      newChangedFields.add(field)

      return {
        curriculum: newCurriculum,
        isModified: true,
        changedFields: newChangedFields,
      }
    })
  },

  updateQuizData: (field, value) => {
    set((state) => {
      const newChangedFields = new Set(state.changedFields)
      newChangedFields.add(field)

      return {
        quizData: { ...state.quizData, [field]: value },
        isModified: true,
        changedFields: newChangedFields,
      }
    })
  },

  resetToOriginal: () => {
    const { originalData } = get()
    if (originalData) {
      get().initializeFromData(originalData)
    }
  },

  markAsSaved: () => {
    set({ isModified: false, changedFields: new Set() })
  },

  getChangedData: () => {
    const { originalData, curriculum, quizData, changedFields } = get()
    if (!originalData) return {}

    const changedData = {
      QUESTION_ID: originalData.QUESTION_ID,
    }

    if (changedFields.has('level')) {
      changedData.LEVEL = curriculum.level
    }
    if (changedFields.has('mainChapter')) {
      changedData.MAIN = curriculum.mainChapter
    }
    if (changedFields.has('subChapter')) {
      changedData.SUB = curriculum.subChapter
    }
    if (changedFields.has('unit')) {
      changedData.UNIT = curriculum.unit
    }
    if (changedFields.has('detailUnit')) {
      changedData.DETAIL_UNIT = curriculum.detailUnit
    }
    if (changedFields.has('no')) {
      changedData.NO = curriculum.no
    }

    if (changedFields.has('question')) {
      changedData.TITLE = quizData.question
    }
    if (changedFields.has('correctAnswer')) {
      changedData.ANSWER1 = quizData.correctAnswer
    }

    if (changedFields.has('correctAnswers')) {
      changedData.ANSWER1 = quizData.correctAnswers[0] || ''
      changedData.ANSWER2 = quizData.correctAnswers[1] || ''
      changedData.ANSWER3 = quizData.correctAnswers[2] || ''
      changedData.ANSWER4 = quizData.correctAnswers[3] || ''
      changedData.ANSWER5 = quizData.correctAnswers[4] || ''
    }

    if (
      changedFields.has('checkedStates') ||
      changedFields.has('isCorrect1') ||
      changedFields.has('isCorrect2') ||
      changedFields.has('isCorrect3') ||
      changedFields.has('isCorrect4') ||
      changedFields.has('isCorrect5')
    ) {
      changedData.IS_CORRECT_1 = quizData.isCorrect1
      changedData.IS_CORRECT_2 = quizData.isCorrect2
      changedData.IS_CORRECT_3 = quizData.isCorrect3
      changedData.IS_CORRECT_4 = quizData.isCorrect4
      changedData.IS_CORRECT_5 = quizData.isCorrect5
    }

    if (changedFields.has('explanation')) {
      changedData.DESC = quizData.explanation
    }

    if (changedFields.has('existingQuestionImageUrl')) {
      changedData.IMG_URL = quizData.existingQuestionImageUrl
    }
    if (changedFields.has('existingSubQuestionImageUrl')) {
      changedData.IMG_URL2 = quizData.existingSubQuestionImageUrl
    }
    if (changedFields.has('existingExplanationImageUrl')) {
      changedData.DESC_URL = quizData.existingExplanationImageUrl
    }

    if (changedFields.has('questionImageFile') && quizData.questionImageFile) {
      changedData.IMG_URL = URL.createObjectURL(quizData.questionImageFile)
    }
    if (
      changedFields.has('explanationImageFile') &&
      quizData.explanationImageFile
    ) {
      changedData.DESC_URL = URL.createObjectURL(quizData.explanationImageFile)
    }

    return changedData
  },
}))

export const QuizType = ['A', 'B', 'C', 'D', 'E', 'F', 'G']

export default useQuizStore
